import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';

import { Clipboard } from '@angular/cdk/clipboard';

import { IconComponent } from '../design-system/components/icon/icon.component';

import {
  AdopterCommunicationChannel,
  AdopterCommunicationPreference,
  AdopterProfileSummary,
} from './adopter-profile-summary.models';
import { LinkoutComponent } from '../linkout/linkout.component';

@Component({
  selector: 'lib-adopter-profile-summary',
  standalone: true,
  imports: [IconComponent, LinkoutComponent, IconComponent],
  templateUrl: './adopter-profile-summary.component.html',
  styleUrl: './adopter-profile-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdopterProfileSummaryComponent {
  readonly adopter = input.required<AdopterProfileSummary>();

  readonly showBackButton = input(false);
  readonly showPetszelId = input(false);
  readonly showProfileEdit = input(false);
  readonly showOperationalDetails = input(false);
  readonly allowEmailEditing = input(false);
  readonly allowPhoneEditing = input(false);
  readonly allowImpersonation = input(false);

  readonly personIdUrl = input<string | null>(null);

  readonly allowPersonIdCopy = input(false);

  readonly showShelterluvLinkoutHelp = input(false);

  readonly back = output<void>();
  readonly addEmail = output<void>();
  readonly editEmail = output<void>();
  readonly editPhone = output<void>();
  readonly editProfile = output<void>();
  readonly impersonate = output<void>();

  readonly isAdopterUnknown = computed(() => {
    return this.adopter()?.firstName?.trim().toLowerCase() === 'unknown';
  });

  private readonly subscriptionOrder = new Map<string, number>([
    ['PostAdoption', 0],
    ['Marketing', 1],
    ['Transactional', 2],
    ['Default', 3],
    ['DoubleOptIn', 4],
  ]);

  private readonly subscriptionLabels = new Map<string, string>([
    ['PostAdoption', 'Post Adoption'],
    ['Marketing', 'Marketing'],
    ['Transactional', 'Transactional'],
    ['Default', 'Default'],
    ['DoubleOptIn', 'Double Opt-In'],
  ]);

  subscriptions(
    channel: AdopterCommunicationChannel,
  ): AdopterCommunicationPreference[] {
    return this.adopter()
      .messageSubscriptions.filter(
        (subscription) => subscription.channelType === channel,
      )
      .slice()
      .sort((left, right) => {
        const leftRank = this.subscriptionOrder.get(left.name) ?? 999;
        const rightRank = this.subscriptionOrder.get(right.name) ?? 999;

        return leftRank - rightRank || left.name.localeCompare(right.name);
      });
  }

  subscriptionLabel(name: string): string {
    const knownLabel = this.subscriptionLabels.get(name);

    if (knownLabel) {
      return knownLabel;
    }

    return name
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .trim();
  }

  statusClass(status: string | null | undefined): string {
    const normalizedStatus = this.normalizeStatus(status);

    if (normalizedStatus === 'allowed') {
      return 'profile-summary__status--allowed';
    }

    if (normalizedStatus === 'pending') {
      return 'profile-summary__status--pending';
    }

    if (['notallowed', 'not allowed', 'denied'].includes(normalizedStatus)) {
      return 'profile-summary__status--not-allowed';
    }

    return '';
  }

  statusLabel(status: string): string {
    const normalizedStatus = this.normalizeStatus(status);

    return normalizedStatus === 'notallowed' ? 'not allowed' : normalizedStatus;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status ?? '').trim().toLowerCase();
  }

  private readonly clipboard = inject(Clipboard);

  copyPersonId(personId: string): void {
    const normalizedPersonId = personId.trim();

    if (!normalizedPersonId) {
      return;
    }

    this.clipboard.copy(normalizedPersonId);
  }
}
